/* global axios */
import ConversationApi from 'dashboard/api/inbox/conversation';
import types from 'dashboard/store/mutation-types';

const deliveries = new WeakMap();
export function fetchRecoveredConversationList(context, offer) {
  const previous = deliveries.get(context.state);
  const same = previous?.current() && previous.route === context.rootState.route && previous.signature === offer.signature && previous.revision === offer.revision && previous.cacheSignature === offer.cacheSignature;
  if (same) return previous.promise;
  const active = { current: offer.current, route: context.rootState.route, signature: offer.signature, revision: offer.revision, cacheSignature: offer.cacheSignature };
  deliveries.set(context.state, active);
  active.promise = fetchCurrentList(context, offer).then(accepted => {
    if (!accepted && deliveries.get(context.state) === active) deliveries.delete(context.state);
    return accepted;
  }, error => { if (deliveries.get(context.state) === active) deliveries.delete(context.state); throw error; });
  return active.promise;
}

async function fetchCurrentList(context, offer) {
  const { state, rootGetters, commit, dispatch } = context;
  const account = rootGetters.getCurrentAccountId;
  const actor = rootGetters.getCurrentUser.id;
  const current = offer.current;
  if (!current()) return false;
  const pages = Math.max(1, Number(offer.pages) || 1);
  if (!Number.isSafeInteger(pages) || pages > 100) throw new Error('Native recovery window exceeds supported bound');
  const records = [];
  const primaryIds = new Set();
  let meta;
  let coherent = true;
  const transport = { timeout: 2500, recovery: true };
  const recordSnapshot = response => {
    const digest = response.headers?.['x-attention-queue-revision'];
    const isolated = response.headers?.['x-attention-snapshot-coherent'];
    if (!/^[a-f0-9]{64}$/.test(digest || '') || !['true', 'false'].includes(isolated)) throw new Error('Invalid native prefix snapshot');
    coherent = coherent && isolated === 'true' && digest === offer.revision;
  };
  // Refresh the already loaded prefix, including a newly qualifying record
  // at its middle. Never advance/rewind the agent's page markers.
  for (let page = 1; page <= pages; page++) {
    if (!current()) return false;
    const response = offer.queryData
      ? await ConversationApi.filter({ queryData: offer.queryData, page }, transport)
      : await ConversationApi.get({ ...offer.params, page }, transport);
    if (!current()) return false;
    recordSnapshot(response);
    const data = offer.queryData ? response.data : response.data.data;
    if (!Array.isArray(data?.payload) || data.payload.some(row => row.account_id !== account || !Number.isSafeInteger(row.id))) throw new Error('Invalid native list scope');
    records.push(...data.payload); data.payload.forEach(row => primaryIds.add(row.id)); meta = data.meta;
    if (!data.payload.length) break;
  }
  // InteliChat already presents pending intake alongside its open queue. Fetch
  // its bounded pending window independently, retaining OPEN metadata counts.
  const additional = offer.extraParams || [];
  if (!Array.isArray(additional) || additional.length > 2) throw new Error('Invalid additional native queue window');
  for (const params of additional) {
    if (!current()) return false;
    const response = await ConversationApi.get({ ...params, page: 1 }, transport);
    if (!current()) return false;
    recordSnapshot(response);
    const { data: { data } } = response;
    if (!Array.isArray(data?.payload) || data.payload.some(row => row.account_id !== account || !Number.isSafeInteger(row.id))) throw new Error('Invalid native pending scope');
    records.push(...data.payload);
  }
  const ids = [...new Set([...state.allConversations.map(row => row.id), ...records.map(row => row.id)])];
  const metadata = [];
  for (let offset = 0; offset < ids.length; offset += 1000) {
    if (!current()) return false;
    const batch = ids.slice(offset, offset + 1000);
    const { data } = await axios.get(`/api/v1/accounts/${account}/attention/native_rows`, { timeout: 2500, params: { ids: batch } });
    if (!current()) return false;
    if (data.account_id !== account || data.actor_id !== actor || !Array.isArray(data.payload) ||
        !Array.isArray(data.requested_ids) || data.requested_ids.length !== batch.length ||
        new Set(data.requested_ids).size !== batch.length || data.requested_ids.some(id => !batch.includes(id)) ||
        data.payload.some(row => row.account_id !== account || !batch.includes(row.id)) ||
        new Set(data.payload.map(row => row.id)).size !== data.payload.length ||
        typeof data.coherent !== 'boolean' || !/^[a-f0-9]{64}$/.test(data.queue_revision || '')) throw new Error('Invalid native row reconciliation');
    metadata.push(...data.payload);
    coherent = coherent && data.coherent && data.queue_revision === offer.revision;
  }
  if (!current() || !offer.cacheCurrent()) return false;
  commit('RECONCILE_ATTENTION_CONVERSATION_LIST', { records, metadata, requestedIds: ids });
  if (!current()) return false;
  const available = new Set(metadata.map(row => row.id));
  const allowedRecords = records.filter(row => available.has(row.id));
  // Inbox access/deletion can change between the list GET and the membership
  // batch. Its authoritative omissions also fence secondary cache delivery.
  if (!offer.queryData && meta && allowedRecords.length === records.length) await dispatch('conversationStats/set', meta);
  if (!current()) return false;
  await dispatch('conversationLabels/setBulkConversationLabels', state.allConversations.filter(row => available.has(row.id)));
  if (!current()) return false;
  commit(`contacts/${types.SET_CONTACTS}`, state.allConversations.filter(row => available.has(row.id)).map(row => row.meta.sender).filter(Boolean));
  const countKey = offer.filter === 'me' ? 'mine_count' : offer.filter === 'unassigned' ? 'unassigned_count' : 'all_count';
  const count = meta?.[countKey];
  if (Number.isSafeInteger(count)) await dispatch('conversationPage/reconcileWindow', { filter: offer.filter, canLoadMore: [...primaryIds].filter(id => available.has(id)).length < count });
  // A different coherent server digest still supplies current useful rows,
  // but cannot mark the offered revision clean. Retry on the next safety sync.
  return current() && coherent;
}
